import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { parseErrorMessage } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Loader2, Send } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { TermsPrivacyNoticeStatus } from "./types";

export function TermsPrivacyNoticePanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const statusQuery = useQuery<TermsPrivacyNoticeStatus>({
    queryKey: ["/api/app/admin/terms-privacy-notice-status"],
    queryFn: async () => {
      const res = await fetch("/api/app/admin/terms-privacy-notice-status", {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load Terms & Privacy notice status");
      return res.json();
    },
    refetchInterval: 60_000,
  });

  const sendMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/app/admin/notify-terms-privacy-update", {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.message ?? "Failed to send Terms & Privacy update emails");
      }
      return res.json() as Promise<{
        success: true;
        eligibleCount: number;
        sent: number;
        failed: number;
        failures: Array<{ email: string; reason: string }>;
      }>;
    },
    onSuccess: (data) => {
      setConfirmOpen(false);
      toast({
        title: "Terms & Privacy notice processed",
        description: `Sent ${data.sent} of ${data.eligibleCount} (${data.failed} failed).`,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/app/admin/terms-privacy-notice-status"],
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Send failed",
        description: parseErrorMessage(err, "Unknown error"),
        variant: "destructive",
      });
    },
  });

  const status = statusQuery.data;
  const lastRun = status?.lastRun ?? null;
  const runs = status?.runs ?? [];
  const eligible = status?.eligibleCount ?? 0;
  const isLoading = statusQuery.isLoading;
  const isSending = sendMutation.isPending;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3
            className="text-lg font-semibold"
            data-testid="text-terms-privacy-notice-title"
          >
            Terms &amp; Privacy Update Notice
          </h3>
          <p className="text-sm text-muted-foreground">
            One-time informational email telling every active user that the Terms of Service
            and Privacy Policy were updated. Purely informational — no action is required and
            no in-app re-acceptance modal is triggered. Use this for silent corrections.
            Each send emails <strong>all</strong> active users (not idempotent).
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => setConfirmOpen(true)}
          disabled={isSending || isLoading}
          data-testid="button-notify-terms-privacy"
        >
          {isSending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Send className="h-4 w-4 mr-2" />
          )}
          {eligible > 0 ? `Send to ${eligible} active users` : "Send notice"}
        </Button>
      </div>

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (isSending) return;
          setConfirmOpen(open);
        }}
      >
        <AlertDialogContent data-testid="dialog-confirm-terms-privacy">
          <AlertDialogHeader>
            <AlertDialogTitle>Send Terms &amp; Privacy update notice?</AlertDialogTitle>
            <AlertDialogDescription data-testid="text-confirm-terms-privacy-description">
              This will immediately email{" "}
              <strong>
                {eligible} active {eligible === 1 ? "user" : "users"}
              </strong>{" "}
              that the Terms of Service and Privacy Policy were updated. This action is
              not idempotent — sending again will email everyone a second time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={isSending}
              data-testid="button-cancel-terms-privacy"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                sendMutation.mutate();
              }}
              disabled={isSending}
              data-testid="button-confirm-terms-privacy"
            >
              {isSending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              {eligible > 0 ? `Send to ${eligible} users` : "Send notice"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : !status ? (
        <Card className="p-6">
          <p className="text-sm text-muted-foreground" data-testid="text-terms-privacy-empty">
            Could not load Terms &amp; Privacy notice status.
          </p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Card className="p-4">
              <div className="text-xs uppercase text-muted-foreground tracking-wide">
                Document Versions
              </div>
              <div
                className="mt-1 text-sm font-semibold"
                data-testid="text-terms-privacy-versions"
              >
                Terms updated {status.termsUpdatedLabel}
              </div>
              <div
                className="text-xs text-muted-foreground mt-1"
                data-testid="text-terms-privacy-privacy-label"
              >
                Privacy updated {status.privacyUpdatedLabel}
              </div>
            </Card>
            <Card className="p-4">
              <div className="text-xs uppercase text-muted-foreground tracking-wide">
                Active Recipients
              </div>
              <div
                className="mt-1 text-2xl font-semibold"
                data-testid="text-terms-privacy-eligible-count"
              >
                {eligible}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Approved, active users with an email address. Every send emails all of them.
              </div>
            </Card>
            <Card className="p-4">
              <div className="text-xs uppercase text-muted-foreground tracking-wide">
                Last Run
              </div>
              {lastRun ? (
                <>
                  <div
                    className="mt-1 text-sm font-medium"
                    data-testid="text-terms-privacy-last-run-summary"
                  >
                    Sent {lastRun.sent} of {lastRun.eligibleCount}
                    {lastRun.failed > 0 ? ` (${lastRun.failed} failed)` : ""}
                  </div>
                  <div
                    className="text-xs text-muted-foreground mt-1"
                    data-testid="text-terms-privacy-last-run-meta"
                  >
                    {new Date(lastRun.finishedAt).toLocaleString()} · triggered by{" "}
                    {lastRun.triggeredBy}
                  </div>
                </>
              ) : (
                <div
                  className="mt-1 text-sm text-muted-foreground"
                  data-testid="text-terms-privacy-no-last-run"
                >
                  No notice has been sent yet.
                </div>
              )}
            </Card>
          </div>

          <Card className="p-4">
            <div className="text-xs uppercase text-muted-foreground tracking-wide">
              Change Summaries (sent in email)
            </div>
            <p className="mt-2 text-xs font-semibold text-muted-foreground">Terms of Service</p>
            <p
              className="mt-1 text-sm whitespace-pre-wrap"
              data-testid="text-terms-privacy-terms-summary"
            >
              {status.termsChangeSummary}
            </p>
            <p className="mt-3 text-xs font-semibold text-muted-foreground">Privacy Policy</p>
            <p
              className="mt-1 text-sm whitespace-pre-wrap"
              data-testid="text-terms-privacy-privacy-summary"
            >
              {status.privacyChangeSummary}
            </p>
          </Card>

          {lastRun && lastRun.failures.length > 0 && (
            <Card className="p-4">
              <div className="text-sm font-semibold mb-2 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                Failed Sends ({lastRun.failures.length})
              </div>
              <div className="rounded border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lastRun.failures.map((f, idx) => (
                      <TableRow
                        key={`${f.email}-${idx}`}
                        data-testid={`row-terms-privacy-failure-${idx}`}
                      >
                        <TableCell
                          className="font-mono text-xs"
                          data-testid={`text-terms-privacy-failure-email-${idx}`}
                        >
                          {f.email}
                        </TableCell>
                        <TableCell
                          className="text-xs text-muted-foreground"
                          data-testid={`text-terms-privacy-failure-reason-${idx}`}
                        >
                          {f.reason}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )}

          <Card className="p-4">
            <div className="text-sm font-semibold mb-2" data-testid="text-terms-privacy-history-title">
              Send History ({runs.length})
            </div>
            {runs.length === 0 ? (
              <p
                className="text-sm text-muted-foreground"
                data-testid="text-terms-privacy-history-empty"
              >
                No notices have been sent yet. Past blasts will appear here and
                survive server restarts.
              </p>
            ) : (
              <div className="rounded border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Triggered by</TableHead>
                      <TableHead className="text-right">Eligible</TableHead>
                      <TableHead className="text-right">Sent</TableHead>
                      <TableHead className="text-right">Failed</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {runs.map((run, idx) => (
                      <TableRow
                        key={`${run.finishedAt}-${idx}`}
                        data-testid={`row-terms-privacy-run-${idx}`}
                      >
                        <TableCell
                          className="text-xs"
                          data-testid={`text-terms-privacy-run-when-${idx}`}
                        >
                          {new Date(run.finishedAt).toLocaleString()}
                        </TableCell>
                        <TableCell
                          className="text-xs text-muted-foreground"
                          data-testid={`text-terms-privacy-run-trigger-${idx}`}
                        >
                          {run.triggeredBy}
                        </TableCell>
                        <TableCell
                          className="text-right text-xs"
                          data-testid={`text-terms-privacy-run-eligible-${idx}`}
                        >
                          {run.eligibleCount}
                        </TableCell>
                        <TableCell
                          className="text-right text-xs"
                          data-testid={`text-terms-privacy-run-sent-${idx}`}
                        >
                          {run.sent}
                        </TableCell>
                        <TableCell
                          className={`text-right text-xs ${
                            run.failed > 0 ? "text-yellow-600 font-medium" : ""
                          }`}
                          data-testid={`text-terms-privacy-run-failed-${idx}`}
                        >
                          {run.failed}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
